import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, getRouteApi } from "@tanstack/react-router";
import { ImageUp } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "react-hot-toast";

import { SeasonsList } from "@/components/settings/seasons-list";
import {
  SettingsRow,
  SettingsSection,
} from "@/components/settings/settings-section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiErrorMessage } from "@/lib/api";
import { can } from "@/lib/session";
import {
  fetchAdminSettings,
  saveSiteSettings,
  storageUrl,
  uploadDefaultIcon,
} from "@/lib/settings-api";
import type {
  SiteSettingsData,
  SiteSettingsDocument,
} from "@/lib/settings-api";

const settingsKey = ["admin", "settings"] as const;

interface SiteForm {
  facebookUrl: string;
  instagramUrl: string;
  twitterUrl: string;
}

const toForm = (data: SiteSettingsData): SiteForm => ({
  facebookUrl: data.facebookUrl ?? "",
  instagramUrl: data.instagramUrl ?? "",
  twitterUrl: data.twitterUrl ?? "",
});

const socialFields = [
  { key: "instagramUrl", label: "Instagram", placeholder: "instagram.com/..." },
  { key: "twitterUrl", label: "X / Twitter", placeholder: "x.com/..." },
  { key: "facebookUrl", label: "Facebook", placeholder: "facebook.com/..." },
] as const;

const settingsRoute = getRouteApi("/admin/settings");

interface SiteSettingsFormProps {
  canEdit: boolean;
  canUpload: boolean;
  onSaved: () => Promise<void>;
  settings: SiteSettingsDocument;
}

const SiteSettingsForm = ({
  canEdit,
  canUpload,
  onSaved,
  settings,
}: SiteSettingsFormProps) => {
  const savedForm = toForm(settings.data);
  const [form, setForm] = useState<SiteForm>(savedForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const { session } = settingsRoute.useRouteContext();

  const dirty = (Object.keys(form) as (keyof SiteForm)[]).some(
    (key) => form[key].trim() !== savedForm[key]
  );

  const update = (key: keyof SiteForm) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!dirty) {
      return;
    }
    setSaving(true);
    await toast
      .promise(
        saveSiteSettings({
          facebookUrl: form.facebookUrl.trim() || null,
          instagramUrl: form.instagramUrl.trim() || null,
          twitterUrl: form.twitterUrl.trim() || null,
          version: settings.version,
        }),
        {
          error: apiErrorMessage,
          loading: "Saving...",
          success: "Site settings saved",
        }
      )
      .catch(() => null);
    setSaving(false);
    await onSaved();
  };

  const uploadIcon = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const [file] = event.target.files ?? [];
    event.target.value = "";
    if (!file) {
      return;
    }
    setUploading(true);
    await toast
      .promise(uploadDefaultIcon(file), {
        error: apiErrorMessage,
        loading: "Uploading icon...",
        success: "Default icon updated",
      })
      .catch(() => null);
    setUploading(false);
    await onSaved();
  };

  const iconSrc = storageUrl(settings.data.defaultIconStoragePath);

  return (
    <div className="flex flex-col gap-10">
      <form className="flex flex-col gap-10" onSubmit={save}>
        <SettingsSection
          description="Linked from the footer of every public page. Leave blank to hide one."
          title="Social links"
        >
          {socialFields.map((field) => (
            <SettingsRow
              htmlFor={field.key}
              key={field.key}
              label={field.label}
            >
              <Input
                disabled={!canEdit}
                id={field.key}
                inputMode="url"
                onChange={(event) => update(field.key)(event.target.value)}
                placeholder={field.placeholder}
                value={form[field.key]}
              />
            </SettingsRow>
          ))}
        </SettingsSection>

        {canEdit ? (
          <div className="flex items-center justify-end gap-3">
            {dirty ? (
              <Button
                onClick={() => setForm(savedForm)}
                type="button"
                variant="ghost"
              >
                Discard
              </Button>
            ) : null}
            <Button disabled={!dirty || saving} type="submit">
              Save changes
            </Button>
          </div>
        ) : null}
      </form>

      <SeasonsList
        canDelete={can(session, "seasons:delete")}
        canEdit={can(session, "seasons:write")}
      />

      <SettingsSection
        description="Used for any show or episode without its own artwork."
        title="Default show icon"
      >
        <div className="flex flex-wrap items-center gap-5 p-5">
          <div className="flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-muted outline outline-1 -outline-offset-1 outline-black/10 dark:outline-white/10">
            {iconSrc ? (
              <img
                alt="Current default show icon"
                className="size-full object-cover"
                height={80}
                src={iconSrc}
                width={80}
              />
            ) : (
              <ImageUp aria-hidden className="size-6 text-muted-foreground" />
            )}
          </div>
          {canUpload ? (
            <div className="flex flex-col gap-1">
              <input
                accept="image/*"
                className="sr-only"
                id="default-icon"
                onChange={uploadIcon}
                ref={fileInput}
                type="file"
              />
              <Button
                disabled={uploading}
                onClick={() => fileInput.current?.click()}
                type="button"
                variant="outline"
              >
                {iconSrc ? "Replace icon" : "Upload icon"}
              </Button>
              <p className="text-sm text-muted-foreground">
                Square images work best.
              </p>
            </div>
          ) : null}
        </div>
      </SettingsSection>
    </div>
  );
};

const SitePage = () => {
  const { session } = settingsRoute.useRouteContext();
  const queryClient = useQueryClient();

  const settingsQuery = useQuery({
    queryFn: fetchAdminSettings,
    queryKey: settingsKey,
  });
  const settings = settingsQuery.data;

  if (settingsQuery.isPending) {
    return <p className="text-sm text-muted-foreground">Loading settings...</p>;
  }

  if (!settings) {
    return (
      <p className="text-sm text-muted-foreground">
        Site settings couldn’t be loaded.
      </p>
    );
  }

  return (
    <SiteSettingsForm
      canEdit={can(session, "settings:write")}
      canUpload={can(session, "uploads:write")}
      key={settings.version}
      onSaved={() => queryClient.invalidateQueries({ queryKey: settingsKey })}
      settings={settings}
    />
  );
};

export const Route = createFileRoute("/admin/settings/site")({
  component: SitePage,
});
